import * as React from 'react';
import {useCallback, useMemo} from 'react';
import type {SimpleTreeViewProps} from '@mui/x-tree-view';
import {SimpleTreeView} from '@mui/x-tree-view';
import type {ThesaurusValue} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {ChevronLeftIcon} from 'components/icons/ChevronLeftIcon';
import {ChevronRightIcon} from 'components/icons/ChevronRightIcon';
import type {TreeNode} from '../types';
import {
  ThesaurusSelectionContext,
  useThesaurusSelectionContextProviderValue,
} from '../contexts/ThesaurusSelectionContext';
import {ThesaurusItem} from './ThesaurusItem';

// Use SimpleTreeViewProps<Multiple> to be able to use controlled
// tree view with empty initial selected
// although only one node may be selected at a time.
type Props = SimpleTreeViewProps<true> & {
  // The data structure
  data: TreeNode[];
  /**
   * Terms (node ids) set.
   * Nodes will be marked in the tree.
   */
  thesaurusValue?: ThesaurusValue;
  /**
   * gets the display code for a node, if any
   */
  getDisplayCode: (node: TreeNode) => string | undefined;
  /**
   * Current focused node id, if any,
   * either node clicked by user in the tree or
   * term clicked in the work.
   */
  focusedNodeId?: string;
  onlyTopLevel?: boolean;
};

export const Thesaurus: React.FC<Props> = ({
  data,
  thesaurusValue,
  getDisplayCode,
  focusedNodeId,
  onlyTopLevel,
  ...props
}) => {
  const {t} = useLocalization();
  const contextValue = useThesaurusSelectionContextProviderValue(
    data,
    thesaurusValue,
    focusedNodeId,
  );
  const renderNode = useCallback(
    (node: TreeNode) => {
      return (
        <ThesaurusItem
          key={node.id}
          itemId={node.id}
          code={getDisplayCode?.(node)}
          label={
            node.deactivated
              ? `${node.label} (${t('general.deactivated')})`
              : node.label
          }
          onlyTopLevel={onlyTopLevel}>
          {!onlyTopLevel && Array.isArray(node.children)
            ? node.children.map(renderNode)
            : null}
        </ThesaurusItem>
      );
    },
    [getDisplayCode, onlyTopLevel, t],
  );

  return (
    <ThesaurusSelectionContext.Provider value={contextValue}>
      <SimpleTreeView
        aria-label="thema thesaurus"
        {...props}
        defaultSelectedItems={undefined}
        selectedItems={focusedNodeId ? [focusedNodeId] : []}
        slots={{
          collapseIcon: ChevronLeftIcon,
          expandIcon: ChevronRightIcon,
          ...props.slots,
        }}
        slotProps={{
          ...props.slotProps,
        }}>
        {useMemo(() => data.map(renderNode), [data, renderNode])}
      </SimpleTreeView>
    </ThesaurusSelectionContext.Provider>
  );
};
